export const dominantResolutionTemplate = {
  type: "grand-staff",
  config: {
    number: 10,
    points: 2,
    inputMode: "cadenceCompletion",
    dominantResolution: true,
    keyLabel: "E Major",
    keySignature: {treble: {type: "sharps", count: 4}, bass: {type: "sharps", count: 4}},
    prompt: "In the key indicated, resolve each given V7 chord to the tonic triad. Use proper voice leading. Chord roots must be in the bass voice in all chords. Use whole notes.",
    cadenceExamples: [
      ["D#5", "F#5", "A5"],
      ["A4", "D#5", "F#5"],
      ["F#4", "A4", "D#5"],
      ["A4", "B4", "D#5"],
    ].map(notes => ({
      given: {treble: {notes}, bass: {notes: ["B2"]}},
      answers: {chord: ["E4", "G#4", "B4"], bass: "E3", leadingTone: "D#4", seventh: "A4"},
    })),
  },
};


// Display must also work when the server omits answer-key fields.
function printedResolutionPair(config, example) {
  const letters = 'CDEFGAB';
  const label = String(config.keyLabel || config.requestedAnalysisLabels?.[1] || '');
  const signature = config.keySignature?.treble || {};
  const entries = signature[1];
  const flat = signature.type === 'flats' || entries?.[0]?.accidental === 'b';
  const count = Array.isArray(entries) ? entries.length : Number(signature.count) || 0;
  const fallbackTonic = (flat ? ['C','F','B','E','A','D','G','C'] : ['C','G','D','A','E','B','F','C'])[count] || 'C';
  const tonic = label.match(/^\s*([A-Ga-g])/)?.[1]?.toUpperCase() || fallbackTonic;
  const dominant = (letters.indexOf(tonic) + 4) % 7;
  const bass = (example.given?.bass?.notes || []).map(pitch => {
    const match = String(pitch).match(/^([A-G])[^0-9-]*(-?\d+)$/);
    return match && {letter:letters.indexOf(match[1]), height:Number(match[2])*7+letters.indexOf(match[1])};
  }).filter(Boolean).sort((a,b) => a.height-b.height)[0];
  const member = bass ? (bass.letter-dominant+7)%7 : 0;
  const figure = {0:'\u01be', 2:'\u01a2', 4:'\u01a3', 6:'\u01a4'}[member] || '\u01be';
  return ['V'+figure, member === 6 ? 'I\u01a0' : 'I'];
}

// Read each printed pair directly from its example, ahead of any cached or
// adapter-generated per-slot labels. Keep the configured score-font glyphs.
export function resolutionPromptLabels(config) {
  const labels = {};
  (config.cadenceExamples || []).forEach((example, index) => {
    const slot = index * 2 + 1;
    const pair = example.romanNumerals || example.answers?.romanNumerals || [];
    const printed = printedResolutionPair(config, example);
    const normalizeLegacy = value => String(value).replaceAll("\u01b4", "\u01a4");
    labels[slot] = normalizeLegacy(pair[0] || printed[0]);
    labels[slot + 1] = normalizeLegacy(pair[1] || printed[1]);
  });
  return labels;
}

// Reuse the standard grand-staff renderer and its direct note placement.
export function resolutionStaffConfig(config) {
  const examples = config.cadenceExamples || [];
  const givenNotes = {}, requestedRomanNumerals = resolutionPromptLabels(config), inputSlots = [];
  examples.forEach((example,i) => {
    const slot = i * 2 + 1;
    givenNotes[slot] = {...example.given, symbol:"whole_note"};
    inputSlots.push(slot+1);

  });
  const positions = {
    treble:{sharps:["F5","C5","G5","D5","A4","E5","B4"],flats:["B4","E5","A4","D5","G4","C5","F4"]},
    bass:{sharps:["F3","C3","G3","D3","A2","E3","B2"],flats:["B2","E3","A2","D3","G2","C3","F2"]},
  };
  const keySignature = Object.fromEntries(["treble","bass"].map(staff => {
    const signature = config.keySignature?.[staff] || {};
    return [staff, signature[1] ? signature : {1:(positions[staff][signature.type] || []).slice(0,signature.count || 0).map(pitch=>({pitch, accidental:signature.type === "flats" ? "b" : "#"}))}];
  }));
  return {...config,inputMode:"notes",slotCount:examples.length*2,measures:examples.length,slotsPerBar:2,
    slotWidth:110,inputSlots,givenNotes,requestedRomanNumerals,keySignature,
    requestedAnalysisLabels:{1:config.keyLabel || "E Major"},labelLeft:-75,
    allowedSymbols:[],noteLimits:config.noteLimits || {treble:3,bass:1}};
}
export function resolutionStaffValue(value = {}, config) {
  return Object.fromEntries((config.cadenceExamples || []).map((_,i)=>[
    i*2+2, {treble:(value?.examples?.[i]?.[2]?.treble || []).filter(Boolean),bass:(value?.examples?.[i]?.[2]?.bass || []).filter(Boolean)}
  ]));
}
export function resolutionFromStaffValue(value, previous = {}, config) {
  return {...previous,examples:Object.fromEntries((config.cadenceExamples || []).map((_,i)=>[
    i,{...previous?.examples?.[i],2:value[i*2+2] || {treble:[],bass:[]}}
  ]))};
}
